import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import { Edit, ArrowUp, ArrowDown, Plus, Trash2, Save, Upload } from "lucide-react";
import adminApi from "../adminApi";
import { backendUrl } from "../../backendUrl";
import "./LockTypes.css";

interface LockType {
  id: number;
  type_code: string;
  name: string;
  icon_path: string | null;
  display_order: number;
  is_active: number | boolean;
  created_at?: string;
}

interface IconFile {
  name: string;
  path: string;
}

function LockTypes() {
  const { t, tf } = useLanguage();
  const { notify, confirm } = useDialogs();
  const [lockTypes, setLockTypes] = useState<LockType[]>([]);
  const [availableIcons, setAvailableIcons] = useState<IconFile[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editTypeCode, setEditTypeCode] = useState("");
  const [uploadingIcon, setUploadingIcon] = useState(false);

  useEffect(() => {
    fetchLockTypes();
    fetchAvailableIcons();
    fetchAvailableTypes();
    // dane raz, przy wejściu do widoku (fetch* to zwykłe funkcje komponentu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailableTypes = async () => {
    try {
      const response = await adminApi.get("/lock-types/available");
      setAvailableTypes(response.data);
    } catch (err) {
      console.error("Error fetching available types:", err);
    }
  };

  const fetchLockTypes = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get("/lock-types");
      setLockTypes(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching lock types:", err);
      setError(t("Failed to load lock types"));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIcons = async () => {
    try {
      const response = await adminApi.get("/lock-types/icons");
      setAvailableIcons(response.data);
    } catch (err) {
      console.error("Error fetching available icons:", err);
    }
  };

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('icon', file);

    try {
      setUploadingIcon(true);
      const response = await adminApi.post(
        "/lock-types/icons",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        await fetchAvailableIcons();
        setEditIcon(response.data.path);
        notify(`${t("Icon uploaded successfully")}: ${response.data.name}`, "success");
      }
    } catch (err) {
      console.error("Error uploading icon:", err);
      notify(t("Failed to upload icon"), "error");
    } finally {
      setUploadingIcon(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleIconDelete = async (iconName: string) => {
    if (!(await confirm({ message: tf("Are you sure you want to delete the icon \"{name}\"?", { name: iconName }), confirmLabel: t("Delete"), danger: true }))) {
      return;
    }

    try {
      await adminApi.delete(`/lock-types/icons/${iconName}`);
      await fetchAvailableIcons();

      // Jeśli usunięta ikona była wybrana w edycji, wyczyść wybór
      if (editIcon === `/img/Types/${iconName}`) {
        setEditIcon("");
      }

      notify(`${t("Icon")} "${iconName}" ${t("deleted successfully")}`, "success");
    } catch (err: any) {
      console.error("Error deleting icon:", err);
      console.error("Error response:", err.response);
      if (err.response?.status === 400 && err.response?.data?.usedBy) {
        notify(`${t("Cannot delete icon. It is still used by:")} ${err.response.data.usedBy.join(', ')}`, "error");
      } else {
        notify(t("Failed to delete icon"), "error");
      }
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newTypes = [...lockTypes];
    [newTypes[index - 1], newTypes[index]] = [newTypes[index], newTypes[index - 1]];

    // Zaktualizuj display_order
    newTypes.forEach((type, idx) => {
      type.display_order = idx;
    });

    setLockTypes(newTypes);
    await saveLockTypes(newTypes);
  };

  const handleMoveDown = async (index: number) => {
    if (index === lockTypes.length - 1) return;
    const newTypes = [...lockTypes];
    [newTypes[index], newTypes[index + 1]] = [newTypes[index + 1], newTypes[index]];

    // Zaktualizuj display_order
    newTypes.forEach((type, idx) => {
      type.display_order = idx;
    });

    setLockTypes(newTypes);
    await saveLockTypes(newTypes);
  };

  const handleEdit = (type: LockType) => {
    setEditingId(type.id);
    setEditTypeCode(type.type_code);
    setEditName(type.name);
    setEditIcon(type.icon_path || "");
  };

  const handleSaveEdit = async () => {
    if (editingId === null) return;

    const updatedTypes = lockTypes.map(type =>
      type.id === editingId
        ? { ...type, name: editName, icon_path: editIcon }
        : type
    );

    setLockTypes(updatedTypes);
    await saveLockTypes(updatedTypes);
    setEditingId(null);
  };

  const handleAdd = async (typeCode: string) => {
    try {
      const response = await adminApi.post("/lock-types", {
        type_code: typeCode,
        name: typeCode,
        icon_path: ""
      });

      if (response.data.success) {
        await fetchLockTypes();
        //alert(`Lock type ${typeCode} added successfully!`);
        setError(null);
      }
    } catch (err: any) {
      console.error("Error adding lock type:", err);
      const errorMsg = err.response?.data?.message || t("Failed to add lock type");
      setError(errorMsg);
      notify(`${t("Error")}: ${errorMsg}`, "error");
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t("Are you sure you want to delete this lock type?"), confirmLabel: t("Delete"), danger: true }))) return;

    try {
      await adminApi.delete(`/lock-types/${id}`);
      await fetchLockTypes();
    } catch (err) {
      console.error("Error deleting lock type:", err);
      setError(t("Failed to delete lock type"));
    }
  };

  const saveLockTypes = async (types: LockType[]) => {
    try {
      await adminApi.put("/lock-types", {
        lockTypes: types
      });
      //alert("Changes saved successfully!");
      setError(null);
    } catch (err) {
      console.error("Error saving lock types:", err);
      setError(t("Failed to save lock types"));
      notify(t("Error: Failed to save lock types"), "error");
    }
  };

  if (loading) return <div className="loading">{t("Loading...")}</div>;
  if (error) return <div className="error">{error}</div>;

  // Filtruj typy które już są przypisane
  const assignedTypeCodes = lockTypes.map(lt => lt.type_code);
  const unassignedTypes = availableTypes.filter(type => !assignedTypeCodes.includes(type));

  return (
    <div className="lock-types">
      <div className="lock-types-header">
        <h2>{t("Lock Types Management")}</h2>
      </div>

      {/* Sekcja z nieprzypisanymi typami */}
      {unassignedTypes.length > 0 && (
        <div className="unassigned-types">
          <h3>{t("Available Types from Database (click to add):")}</h3>
          <div className="type-badges">
            {unassignedTypes.map(typeCode => (
              <button
                key={typeCode}
                className="type-badge"
                onClick={() => handleAdd(typeCode)}
              >
                <Plus size={14} /> {typeCode}
              </button>
            ))}
          </div>
        </div>
      )}

      {lockTypes.length === 0 ? (
        <div className="empty-state">
          <p>{t("No lock types configured yet. Add types from the list above.")}</p>
        </div>
      ) : (
        <ul>
          {lockTypes.map((type, index) => (
            <li key={type.id} className="lock-type-item">
              {editingId === type.id ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t("Type Code (from database):")}</label>
                    <input
                      type="text"
                      value={editTypeCode}
                      disabled
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Display Name:")}</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={t("Display name (e.g., Doppelzylinder)")}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Icon:")}</label>
                    <div className="icon-selector">
                      <select
                        value={editIcon}
                        onChange={(e) => setEditIcon(e.target.value)}
                        className="icon-dropdown"
                      >
                        <option value="">{t("-- Select Icon --")}</option>
                        {availableIcons.map((icon) => (
                          <option key={icon.path} value={icon.path}>
                            {icon.name}
                          </option>
                        ))}
                      </select>

                      <label className="upload-btn">
                        <Upload size={16} />
                        {uploadingIcon ? t("Uploading...") : t("Upload New")}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleIconUpload}
                          style={{ display: 'none' }}
                          disabled={uploadingIcon}
                        />
                      </label>
                    </div>

                    {/* Icon Management Section */}
                    {availableIcons.length > 0 && (
                      <div className="icon-management">
                        <h4>{t("Available Icons:")}</h4>
                        <div className="icon-grid">
                          {availableIcons.map((icon) => (
                            <div key={icon.path} className="icon-item">
                              <img
                                src={`${backendUrl}${icon.path}`}
                                alt={icon.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <span className="icon-name">{icon.name}</span>
                              <button
                                className="delete-icon-btn"
                                onClick={() => handleIconDelete(icon.name)}
                                title={`${t("Delete")} ${icon.name}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {editIcon && (
                      <div className="icon-preview">
                        <img
                          src={`${backendUrl}${editIcon}`}
                          alt={t("Icon preview")}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button className="btn-save" onClick={handleSaveEdit}>
                      <Save size={16} /> {t("Save")}
                    </button>
                    <button className="btn-cancel" onClick={() => setEditingId(null)}>{t("Cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="lock-type-info">
                    {type.icon_path && (
                      <div className="icon-display">
                        <img
                          src={`${backendUrl}${type.icon_path}`}
                          alt={type.name}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="type-details">
                      <span className="lock-type-name">{type.name}</span>
                      <span className="lock-type-code">{t("Code:")} {type.type_code}</span>
                      {type.icon_path && (
                        <span className="lock-type-icon-name">
                          {type.icon_path.split('/').pop()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="lock-type-actions">
                    <button className="edit-btn" onClick={() => handleEdit(type)}>
                      <Edit size={16} /> {t("Edit")}
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === lockTypes.length - 1}
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(type.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LockTypes;

